import React = require('react');
import { cloneDeep, flow, get, isEmpty, last } from 'lodash';
import { connect } from 'react-redux';
import { IStore } from '../../../../../../../../redux/IStore';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { mtaActions } from '../../../../../../../../services/mta/actions';
import { ICustomerMTAOptions } from '../../../../index';
import { IMtaActions, IMtaFormNames, IMtaState } from '../../../../../../../../services/mta/models';
import { reduxForm } from 'redux-form';
import { canToleratePremium } from '../../../../../../../../helpers/Mta';
import { MTAPremiumMismatch } from '../Components/MTAPremiumMismatch';
import { MTAPremiumHasReferrals } from '../Components/MTAPremiumHasReferrals';
import { useEffect, useState } from 'react';
import { MtaSelection } from '../../../MakeAChangeSelection';
import { LoadingSpinner } from '../../../../../../../../components/LoadingSpinner';

interface IMtaSelectProps {
  mta: IMtaState;
  mtaPolicy: any;
  change: any;
  dispatch: any;
  customerMTAOptions: ICustomerMTAOptions;
  actions: {
    push(path: string);
    mta: IMtaActions;
  };
}

const MtaSelect = ({ mta, actions, dispatch, change, mtaPolicy }: IMtaSelectProps) => {

  const [isLoading, setIsLoading] = useState(true);
  const [premiumMismatch, setPremiumMismatch] = useState(false);
  const [hasReferrals, setHasReferrals] = useState(false);


  useEffect(() => {
    const promises = [];

    promises.push(dispatch(change('data.attributes', mtaPolicy)));
    promises.push(actions.mta.checkIfHasWSExclusion());

    Promise.all(promises).then(() => {
      actions.mta.calculateMtaPremiums().then(() => {
        setIsLoading(false);
        checkPremiumMismatch();

      }).catch(() => {
        setIsLoading(false);
      });
    }).catch(() => {
      setIsLoading(false);
    })

  }, [])

  const handleSelection = (selection: string) => {
    actions.mta.setSelectedMta(selection);
    switch (selection) {
      case MtaSelection.ADDITIONAL_OPTION:
        actions.push(`/account/make-a-change/mta/additional-cover`);
        break;
      case MtaSelection.ADD_TRAVELLER:
        actions.push(`/account/make-a-change/mta/additional-travellers`);
        break;
      case MtaSelection.ADD_MEDICAL:
        actions.push(`/account/make-a-change/mta/medical-declaration`);
        break;
      case MtaSelection.CHANGE_REGION:
        actions.push(`/account/make-a-change/mta/change-region`);
        break;
      case MtaSelection.CHANGE_DESTINATION:
        actions.push(`/account/make-a-change/mta/change-destination`);
        break;
      case MtaSelection.CHANGE_TRIP_DATE:
        actions.push(`/account/make-a-change/mta/change-trip-date`);
        break;
      case MtaSelection.REMOVE_TRAVELLER:
        actions.push(`/account/make-a-change/mta/remove-traveller`);
        break;
      case MtaSelection.CHANGE_TIER:
        actions.push(`/account/make-a-change/mta/change-tier`);
        break;
      case MtaSelection.CHANGE_TRIP_DURATION:
        actions.push(`/account/make-a-change/mta/change-trip-duration`);
        break;
      case MtaSelection.REMOVE_OPTION:
        actions.push(`/account/make-a-change/mta/remove-option`);
        break;
    }
  }

  const checkPremiumMismatch = () => {
    const selectedScheme = actions.mta.getSelectedScheme();
    const grossPremium = get(selectedScheme, 'premium.gross');
    const hasReferrals = !isEmpty(get(selectedScheme, 'referrals'));
    if (selectedScheme && hasReferrals) {
      setHasReferrals(true);
    } else if (selectedScheme && canToleratePremium(grossPremium) === false) {
      setPremiumMismatch(true);
    } else if (!selectedScheme) {
      setPremiumMismatch(true);
    }
  }

  const redirectToMTAChange = () => {
    handleSelection(mta?.selectedMTAChange?.mtaName)
  }

  if (isLoading) {
    return (
      <LoadingSpinner text={'Getting your policy details...'}/>
    )
  }

  if (premiumMismatch && !isLoading) {
    return (
      <>
        {/*  This is a failsafe addon if the premium is not zero at this point for some reason */}
        <div className={'mt-30'}/>
        <MTAPremiumMismatch/>
      </>)
  }

  if (hasReferrals && !isLoading) {
    return (
      <>
        <div className={'mt-30'}/>
        <MTAPremiumHasReferrals/>
      </>)
  }

  if (!premiumMismatch && !hasReferrals && !isLoading) {
    redirectToMTAChange()
  }

  return null;
}

export default flow([
  reduxForm({
    form: IMtaFormNames.COVER_DETAILS,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    return component;
  },
  connect(
    (state: IStore) => {
      const mtaPolicy = cloneDeep(state.mta.selectedPolicy);
      const latestPremium = last(mtaPolicy.premiums);
      mtaPolicy.effective_date = new Date();
      mtaPolicy.metadata.portal_mta = true;
      mtaPolicy.mta = true;
      mtaPolicy.scheme_id = latestPremium ? latestPremium.scheme.id : null;
      return {
        mtaPolicy,
        mta: state.mta,
        customerMTAOptions: get(state.branding.channel.products[0], 'metadata.direct_customer_mta_options', null),
      };
    },
    (dispatch) => ({
      actions: {
        push: bindActionCreators(push, dispatch),
        mta: bindActionCreators({ ...mtaActions }, dispatch),
      },
    }),
  ),
])(MtaSelect);
